import React, { useRef, useState } from "react";
import logger from "../lib/logger";
import html2canvas from "html2canvas";
import { motion } from "framer-motion";
import { Eyebrow, Btn } from "../components/Primitives";
import Logo from "../components/Logo";
import NotificationBell from "../components/NotificationBell";
import ListenButton from "../components/ListenButton";
import { toast } from "sonner";

export default function FullReveal({ archetype, idealPartner, user, onBuildProfile, onBrowseMatches, onOpenDating, onOpenNotifications, onOpenCoach, onOpenConcierge, onOpenMembership }) {
    const cardRef = useRef(null);
    const [generating, setGenerating] = useState(false);

    const share = async () => {
        if (!cardRef.current) return;
        setGenerating(true);
        try {
            const canvas = await html2canvas(cardRef.current, {
                backgroundColor: "#FAF6F1",
                scale: 2,
                useCORS: true,
            });
            const dataUrl = canvas.toDataURL("image/png");
            const blob = await (await fetch(dataUrl)).blob();

            // Try native share first
            if (navigator.share && navigator.canShare && navigator.canShare({ files: [new File([blob], "ever-after.png", { type: "image/png" })] })) {
                const file = new File([blob], "ever-after.png", { type: "image/png" });
                try {
                    await navigator.share({
                        files: [file],
                        title: "My Ever After archetype",
                        text: archetype.name,
                    });
                    return;
                } catch (err) {
                    logger.warn("native share failed, will fallback to download:", err);
                }
            }
            // Fallback: download
            const a = document.createElement("a");
            a.href = dataUrl;
            a.download = `ever-after-${archetype.id}.png`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            toast.success("Card saved");
        } catch (e) {
            toast.error("Could not generate share card");
        } finally {
            setGenerating(false);
        }
    };

    return (
        <div className="flex-1 flex flex-col bg-cream" data-testid="full-reveal-screen">
            {onOpenNotifications && (
                <div className="px-6 pt-5 flex justify-end">
                    <NotificationBell user={user} onOpen={onOpenNotifications} />
                </div>
            )}
            <div className="px-7 pt-6 pb-8">
                <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7 }}
                >
                    <Eyebrow className="text-gold-deep">Unlocked · Your archetype</Eyebrow>
                    <h1 className="mt-3 font-display text-5xl sm:text-6xl leading-[1.05] text-burgundy tracking-tight">
                        <span className="italic">{archetype.name.split(" ").slice(0, 2).join(" ")}</span>{" "}
                        {archetype.name.split(" ").slice(2).join(" ")}
                    </h1>
                    <p className="mt-5 font-display italic text-xl text-ink leading-snug">
                        {archetype.tagline}
                    </p>
                    <div className="mt-6 editorial-rule" />
                </motion.div>
            </div>

            <div className="px-7 space-y-10 pb-10">
                <section>
                    <div className="flex items-center justify-between gap-3">
                        <Eyebrow>Your full profile</Eyebrow>
                        <ListenButton
                            sessionId={user?.session_id}
                            text={`${archetype.name}. ${archetype.tagline || ""} ${archetype.full_description || ""}`}
                            label="Listen"
                            testId="listen-profile-btn"
                        />
                    </div>
                    <p className="mt-3 font-display text-xl leading-relaxed text-ink whitespace-pre-line">
                        {archetype.full_description}
                    </p>
                </section>

                <section>
                    <Eyebrow>Your ideal partner</Eyebrow>
                    <p className="mt-3 font-display text-3xl text-burgundy">
                        {idealPartner?.name || archetype.ideal_partner_id}
                    </p>
                    {idealPartner?.full_description && (
                        <p className="mt-3 font-display text-lg leading-relaxed text-ink-muted">
                            {idealPartner.full_description.split("\n\n")[0]}
                        </p>
                    )}
                </section>

                <section>
                    <Eyebrow>Why you keep attracting this pattern</Eyebrow>
                    <p className="mt-3 font-display text-xl leading-relaxed text-ink whitespace-pre-line">
                        {archetype.attracting_pattern}
                    </p>
                </section>

                <section>
                    <Eyebrow>Your compatibility blind spots</Eyebrow>
                    <ul className="mt-3 space-y-2 font-display text-lg text-ink">
                        {archetype.blind_spots.map((b) => (
                            <li key={b} className="flex gap-2">
                                <span className="text-gold-deep">·</span>
                                <span>{b}</span>
                            </li>
                        ))}
                    </ul>
                </section>

                <section className="border border-burgundy/15 bg-cream-soft p-6">
                    <Eyebrow>Your next step</Eyebrow>
                    <h3 className="mt-3 font-display text-2xl text-burgundy">
                        <span className="italic">Compatible matches</span> are being curated for you.
                    </h3>
                    <p className="mt-3 text-sm text-ink-muted leading-relaxed">
                        {user?.profile_completed
                            ? "Your profile is ready. We send you 3 hand-picked compatible profiles every Monday."
                            : "Add your profile and your partner preferences. Without these, we can't curate the right matches for you."}
                    </p>
                    <div className="mt-5 space-y-2">
                        {onBuildProfile && (
                            <Btn data-testid="cta-build-profile" variant={user?.profile_completed ? "secondary" : "primary"} onClick={onBuildProfile}>
                                {user?.profile_completed ? "Edit my profile" : "Build my match profile"}
                            </Btn>
                        )}
                        {onBrowseMatches && (
                            <Btn data-testid="cta-browse-matches" variant={user?.profile_completed ? "primary" : "secondary"} onClick={onBrowseMatches} disabled={!user?.profile_completed}>
                                See this week's 3 picks
                            </Btn>
                        )}
                        {onOpenDating && (
                            <Btn data-testid="cta-open-dating" variant="secondary" onClick={onOpenDating}>
                                Your dates & match planning
                            </Btn>
                        )}
                    </div>
                </section>

                <section className="border border-burgundy/15 bg-cream p-6">
                    <Eyebrow>Always-on guidance</Eyebrow>
                    <h3 className="mt-3 font-display text-2xl text-burgundy">
                        <span className="italic">Your AI coach</span> and <span className="italic">concierge.</span>
                    </h3>
                    <p className="mt-3 text-sm text-ink-muted leading-relaxed">
                        Two AI assistants for the parts of dating that aren't on an app — the conversations, the strategy, the romance.
                    </p>
                    <div className="mt-5 space-y-2">
                        {onOpenCoach && (
                            <Btn data-testid="cta-open-coach" variant="primary" onClick={onOpenCoach}>
                                Talk to my dating coach
                            </Btn>
                        )}
                        {onOpenConcierge && (
                            <Btn data-testid="cta-open-concierge" variant="secondary" onClick={onOpenConcierge}>
                                Ask my dating concierge
                            </Btn>
                        )}
                    </div>
                </section>

                {onOpenMembership && (
                    <button
                        data-testid="cta-open-membership"
                        onClick={onOpenMembership}
                        className="tap-target text-[11px] tracking-[0.28em] uppercase text-burgundy/70 hover:text-burgundy underline underline-offset-4 self-start"
                    >
                        Founding membership settings →
                    </button>
                )}

                <a
                    href="/tiers"
                    data-testid="cta-browse-tiers"
                    className="tap-target text-[11px] tracking-[0.28em] uppercase text-burgundy/70 hover:text-burgundy underline underline-offset-4 self-start"
                >
                    Browse all membership tiers →
                </a>

                {/* Hidden share card layout (rendered, captured offscreen position via fixed) */}
                <div className="overflow-hidden">
                    <div
                        ref={cardRef}
                        data-testid="share-card"
                        className="w-[400px] aspect-[4/5] bg-cream border border-burgundy/20 p-8 mx-auto flex flex-col relative"
                        style={{ maxWidth: "100%" }}
                    >
                        <div className="flex items-center gap-3">
                            <Logo size={64} />
                            <div className="font-display italic text-burgundy text-2xl">Ever After</div>
                        </div>
                        <div className="mt-1 text-[10px] tracking-[0.32em] uppercase text-ink-muted">
                            My archetype
                        </div>

                        <div className="flex-1 flex flex-col justify-center">
                            <h2 className="font-display text-4xl leading-[1.05] text-burgundy">
                                <span className="italic">{archetype.name.split(" ").slice(0, 2).join(" ")}</span>{" "}
                                {archetype.name.split(" ").slice(2).join(" ")}
                            </h2>
                            <p className="mt-4 font-display italic text-base text-ink leading-snug">
                                {archetype.tagline}
                            </p>
                        </div>

                        <div className="flex items-center gap-2 text-gold mb-2">
                            <span className="h-px flex-1 bg-burgundy/15" />
                            <span className="font-display italic text-sm">·</span>
                            <span className="h-px flex-1 bg-burgundy/15" />
                        </div>
                        <div className="flex justify-between text-[9px] tracking-[0.32em] uppercase text-ink-muted">
                            <span>youreverafter.com</span>
                            <span>find yours</span>
                        </div>
                    </div>
                </div>

                <Btn data-testid="share-archetype-btn" onClick={share} disabled={generating}>
                    {generating ? "Creating card…" : "Share my archetype"}
                </Btn>

                <section
                    className="border border-burgundy/15 bg-cream-soft p-6 -mt-2"
                    data-testid="share-rewards"
                >
                    <Eyebrow>Spread the word</Eyebrow>
                    <h3 className="mt-3 font-display text-2xl text-burgundy leading-snug">
                        <span className="italic">Tag us</span> when you share.
                    </h3>
                    <p className="mt-3 text-sm text-ink leading-relaxed">
                        Posting on Instagram or TikTok? Tag{" "}
                        <a
                            data-testid="share-ig-link"
                            href="https://instagram.com/joineverafter"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="tap-link text-burgundy underline underline-offset-4"
                        >
                            @joineverafter
                        </a>{" "}
                        on{" "}
                        <a
                            data-testid="share-tiktok-link"
                            href="https://tiktok.com/@joineverafter"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="tap-link text-burgundy underline underline-offset-4"
                        >
                            TikTok
                        </a>{" "}
                        — we re-share the best ones.
                    </p>

                    <div className="my-4 editorial-rule" />

                    <div className="text-[10px] tracking-[0.32em] uppercase text-gold-deep">Refer 3 · Get 3</div>
                    <p className="mt-2 text-sm text-ink leading-relaxed">
                        <span className="font-display italic text-burgundy">Send 3 referrals</span> and win a complimentary{" "}
                        <span className="font-semibold text-burgundy">3-month membership</span> — valued at $99 to $500 depending on your tier when we launch.
                    </p>
                    {user?.referral_code && (
                        <div className="mt-4 p-3 bg-cream border border-burgundy/15 flex items-center justify-between gap-2">
                            <div className="min-w-0">
                                <div className="text-[9px] tracking-[0.32em] uppercase text-ink-light">Your referral link</div>
                                <div className="font-mono text-sm text-burgundy truncate" data-testid="share-referral-link">
                                    youreverafter.com/r/{user.referral_code}
                                </div>
                            </div>
                            <button
                                data-testid="share-copy-referral-btn"
                                onClick={async () => {
                                    try {
                                        await navigator.clipboard.writeText(`https://youreverafter.com/r/${user.referral_code}`);
                                        toast.success("Link copied — paste it anywhere");
                                    } catch (err) {
                                        logger.warn("clipboard write failed:", err);
                                        toast.error("Couldn't copy — long-press the link to share");
                                    }
                                }}
                                className="tap-target text-[10px] tracking-[0.24em] uppercase text-burgundy underline underline-offset-4 shrink-0"
                            >
                                Copy
                            </button>
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
}
